import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import Coin from '@sorare/core/src/atoms/icons/Coin';
import { ArenaTicket } from '@sorare/core/src/atoms/icons/rivals/branded/ArenaTicket';
import { Reputation } from '@sorare/core/src/atoms/icons/rivals/branded/Reputation';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { isType } from '@sorare/core/src/gql';

import { RewardPreviewWrapper } from 'components/rivals/Arena/Challenge/RewardPreview/RewardPreviewWrapper';
import { RivalsLeagueBoxOddsDialog } from 'components/rivalsUserGroup/RivalsLeagueBoxOddsDialog';

import {
  RivalsRewardPreview_managerTaskRewardInterface,
  RivalsRewardPreview_rewardConfig,
} from './__generated__/index.graphql';

const BoxPicture = styled.img`
  height: var(--triple-unit);
  object-fit: cover;
`;

type ImagePreviewProps = {
  probabilisticBundle: Nullable<
    RivalsRewardPreview_rewardConfig['probabilisticBundleConfiguration']
  >;
  arenaTickets?: Nullable<number>;
  coins?: Nullable<number>;
  reputation?: number;
  small?: boolean;
};
const ImagePreview = ({
  probabilisticBundle,
  arenaTickets,
  coins,
  reputation,
  small,
}: ImagePreviewProps) => {
  const [boxOddsDialogOpen, setBoxOddsDialogOpen] = useState(false);
  if (probabilisticBundle) {
    return (
      <>
        <ButtonBase onClick={() => setBoxOddsDialogOpen(true)}>
          <BoxPicture src={probabilisticBundle.pictureUrl} alt="" />
        </ButtonBase>
        <RivalsLeagueBoxOddsDialog
          onClose={() => setBoxOddsDialogOpen(false)}
          open={boxOddsDialogOpen}
          probabilisticBundleConfig={probabilisticBundle}
        />
      </>
    );
  }
  if (reputation) {
    return <Reputation height={small ? 20 : 24} count={reputation} />;
  }
  if (arenaTickets) {
    return <ArenaTicket width={small ? 12 : 14} count={arenaTickets} />;
  }
  if (coins) {
    return <Coin />;
  }
  return null;
};

type RewardDescriptionProps = {
  probabilisticBundle: Nullable<
    RivalsRewardPreview_rewardConfig['probabilisticBundleConfiguration']
  >;
  arenaTickets?: Nullable<number>;
  coins?: Nullable<number>;
  reputation?: number;
};
const RewardDescription = ({
  probabilisticBundle,
  arenaTickets,
  coins,
  reputation,
}: RewardDescriptionProps) => {
  if (probabilisticBundle) {
    return probabilisticBundle.name;
  }
  if (reputation) {
    return (
      <FormattedMessage
        id="MissionsPage.DailyChallenge.repReward"
        defaultMessage="{nb, plural, =0 {{nb} Rep Point} one {{nb} Rep Point} other {{nb} Rep Points}}"
        values={{ nb: reputation }}
      />
    );
  }
  if (arenaTickets) {
    return (
      <FormattedMessage
        id="MissionsPage.DailyChallenge.arenaTicketReward"
        defaultMessage="{nb, plural, =0 {{nb} Arena Ticket} one {{nb} Arena Ticket} other {{nb} Arena Tickets}}"
        values={{ nb: arenaTickets }}
      />
    );
  }
  if (coins) {
    return (
      <FormattedMessage
        id="MissionReward.coin.tooltip"
        defaultMessage="Coin x{count}"
        values={{ count: coins }}
      />
    );
  }
  return null;
};

type Props = {
  reward:
    | RivalsRewardPreview_managerTaskRewardInterface
    | RivalsRewardPreview_rewardConfig;
  showDescription?: boolean;
};
export const RivalsRewardPreview = ({ reward, showDescription }: Props) => {
  let probabilisticBundle;
  let arenaTickets;
  let coins;
  let reputation;

  if (isType(reward, 'RewardConfig')) {
    arenaTickets = reward.arenaTicketAmount;
    coins = reward.coinAmount;
    probabilisticBundle = reward.probabilisticBundleConfiguration;
  } else {
    if (isType(reward, 'ArenaTicketReward')) {
      arenaTickets = reward.count;
    }
    if (isType(reward, 'ReputationReward')) {
      reputation = reward.count;
    }
    if (isType(reward, 'ProbabilisticBundleConfigReward')) {
      probabilisticBundle = reward.probabilisticBundleConfig;
    }
  }

  if (showDescription) {
    return (
      <Horizontal>
        <RewardPreviewWrapper>
          <ImagePreview
            probabilisticBundle={probabilisticBundle}
            arenaTickets={arenaTickets}
            coins={coins}
            reputation={reputation}
            small
          />
        </RewardPreviewWrapper>
        <LabelM as="span">
          <RewardDescription
            probabilisticBundle={probabilisticBundle}
            arenaTickets={arenaTickets}
            coins={coins}
            reputation={reputation}
          />
        </LabelM>
      </Horizontal>
    );
  }

  return (
    <Tooltip
      title={
        <LabelM>
          <RewardDescription
            probabilisticBundle={probabilisticBundle}
            arenaTickets={arenaTickets}
            coins={coins}
            reputation={reputation}
          />
        </LabelM>
      }
    >
      <ImagePreview
        probabilisticBundle={probabilisticBundle}
        arenaTickets={arenaTickets}
        coins={coins}
        reputation={reputation}
      />
    </Tooltip>
  );
};

RivalsRewardPreview.fragments = {
  managerTaskRewardInterface: gql`
    fragment RivalsRewardPreview_managerTaskRewardInterface on ManagerTaskRewardInterface {
      ... on ArenaTicketReward {
        count
      }
      ... on ReputationReward {
        count
      }
      ... on ProbabilisticBundleConfigReward {
        probabilisticBundleConfig {
          id
          pictureUrl
          name
          ...RivalsLeagueBoxOddsDialog_probabilisticBundleConfig
        }
      }
    }
    ${RivalsLeagueBoxOddsDialog.fragments.probabilisticBundleConfig}
  ` as TypedDocumentNode<RivalsRewardPreview_managerTaskRewardInterface>,
  rewardConfig: gql`
    fragment RivalsRewardPreview_rewardConfig on RewardConfig {
      arenaTicketAmount
      coinAmount
      probabilisticBundleConfiguration {
        id
        pictureUrl
        name
        ...RivalsLeagueBoxOddsDialog_probabilisticBundleConfig
      }
    }
    ${RivalsLeagueBoxOddsDialog.fragments.probabilisticBundleConfig}
  ` as TypedDocumentNode<RivalsRewardPreview_rewardConfig>,
};
